<template>
  <!-- Photos (image-grid) as grid, masonry, carousel or filmstrip, with the
       lightbox (captions and "3 of 24") on tap (S_Gallery). -->
  <SectionBand :heading="section.title || ''" :variant="variant" :background="section.background" label="Photos">
    <div v-if="layout === 'carousel'" class="s2-carousel">
      <button type="button" class="s2-carousel__nav" aria-label="Previous photo" @click="step(-1)">‹</button>
      <div ref="track" class="s2-carousel__track" tabindex="0" aria-label="Photos, scroll sideways">
        <Media v-for="(m, i) in items" :key="i" :media="m.raw" :caption="captions" :zoom="zoom" ratio="4 / 3" class="s2-carousel__item" @zoom="open(i)" />
      </div>
      <button type="button" class="s2-carousel__nav" aria-label="Next photo" @click="step(1)">›</button>
    </div>
    <div v-else class="s2-photos mo-stagger" :data-layout="layout">
      <Media v-for="(m, i) in items" :key="i" :media="m.raw" :caption="captions" :zoom="zoom" :ratio="layout === 'grid' ? '1 / 1' : layout === 'filmstrip' ? '3 / 4' : ''" @zoom="open(i)" />
    </div>
    <PhotoLightbox ref="lb" :items="items.filter((m) => m.kind === 'image')" />
  </SectionBand>
</template>

<script setup>
import {computed, ref} from "vue";
import SectionBand from "../shared/SectionBand.vue";
import Media from "../shared/Media.vue";
import PhotoLightbox from "../shared/PhotoLightbox.vue";
import {mediaOf} from "@/utils/blocks.js";

const props = defineProps({section: {type: Object, required: true}, variant: {type: [Number, String], default: 1}});
const layout = computed(() => (["grid", "masonry", "carousel", "filmstrip"].includes(props.section.layout) ? props.section.layout : "grid"));
const captions = computed(() => props.section.captions === true || props.section.showCaptions === true);
const zoom = computed(() => props.section.zoom !== false);
const items = computed(() => (Array.isArray(props.section.images) ? props.section.images : []).map((raw) => ({raw, ...mediaOf(raw)})).filter((m) => m.kind));
const lb = ref(null);
const track = ref(null);
function open(i) {
  const src = items.value[i]?.src;
  const imgs = items.value.filter((m) => m.kind === "image");
  lb.value?.show(Math.max(0, imgs.findIndex((m) => m.src === src)));
}
function step(d) {
  const el = track.value;
  if (!el) return;
  el.scrollBy({left: d * el.clientWidth * 0.8, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"});
}
</script>

<style scoped>
.s2-photos[data-layout="grid"] { display: grid; gap: var(--t-gap); grid-template-columns: repeat(auto-fill, minmax(min(220px, 100%), 1fr)); }
.s2-photos[data-layout="masonry"] { columns: 3 220px; column-gap: var(--t-gap); }
.s2-photos[data-layout="masonry"] > * { break-inside: avoid; margin-bottom: var(--t-gap); }
.s2-photos[data-layout="filmstrip"] { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(200px, 24%); gap: var(--t-gap); overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: .5rem; }
.s2-photos[data-layout="filmstrip"] > * { scroll-snap-align: start; }
.s2-carousel { display: flex; align-items: center; gap: .75rem; }
.s2-carousel__track { flex: 1; display: grid; grid-auto-flow: column; grid-auto-columns: 82%; gap: var(--t-gap); overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; }
.s2-carousel__track::-webkit-scrollbar { display: none; }
.s2-carousel__item { scroll-snap-align: center; }
.s2-carousel__nav { flex: none; width: 44px; height: 44px; border-radius: 999px; border: 1.5px solid var(--t-line); background: var(--t-surface); color: var(--t-ink); font-size: 1.4rem; cursor: pointer; }
@media (max-width: 640px) {
  .s2-carousel__nav { display: none; }
  .s2-photos[data-layout="filmstrip"] { grid-auto-columns: 70%; }
  .s2-photos[data-layout="masonry"] { columns: 2 140px; }
}
</style>
